import {
  Box,
  Button,
  Checkbox,
  Container,
  Flashbar,
  FlashbarProps,
  FormField,
  Header,
  Input,
  Select,
  SpaceBetween,
  Table,
  TableProps,
  Toggle
} from '@amzn/awsui-components-core';
import { FormikProps } from 'formik';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import CustomQuillEditor from 'src/components/generic-components/quill-editor';
import { QuillConfig } from 'src/components/generic-components/quill-editor/types';
import { characterCountConstraintMessage } from 'src/utilities/CommonUtilities';
import { sanitizeHtml } from 'src/utilities/SanitizeHtml';
import { WelcomeContentDetailsEntity } from '../../AdminModels';
import { WELCOME_TITLE_MAX_LENGTH } from '../../applications/admin-manage-application/ApplicationFormValidations';
import SingleEmployeeLookup, { SelectedEmployee } from 'src/components/generic-components/employee-lookup/SingleEmployeeLookup';
import { HomePageWizard } from '../HomepageModel';
import { v4 as uuidv4 } from 'uuid';
import './HomePageStep1.scss';

const BANNER_TYPE_OPTIONS = [
  { label: 'Info', value: 'info' },
  { label: 'Warning', value: 'warning' },
  { label: 'Error', value: 'error' },
  { label: 'Success', value: 'success' }
];

const BANNER_QUILL_CONFIG: QuillConfig = {
  modules: {
    toolbar: {
      container: [['bold', 'italic', 'underline'], ['link']]
    }
  },
  formats: ['bold', 'italic', 'underline', 'link']
};

// ── URL validation helper ──
const isValidUrl = (url: string): boolean => {
  if (!url || url.trim().length === 0) return true; // empty is allowed (optional)
  return /^https?:\/\/.+/.test(url.trim());
};

interface HomePageContentStep1Props {
  formik: FormikProps<HomePageWizard>;
}

const HomePageContentStep1: React.FC<HomePageContentStep1Props> = ({ formik }) => {
  const welcomeContentDetails = formik.values?.homePageDetailsAndWidgets?.welcomeContentDetails ?? [];
  const [selectedItems, setSelectedItems] = useState<WelcomeContentDetailsEntity[]>([]);

  const formikRef = useRef(formik);
  formikRef.current = formik;

  const setItems = useCallback((updated: WelcomeContentDetailsEntity[]) => {
    formikRef.current.setFieldValue('homePageDetailsAndWidgets.welcomeContentDetails', updated);
  }, []);

  /** Update a specific field on a row (used for non-editConfig columns like the employee lookup) */
  const handleUpdateField = useCallback((itemId: string, field: string, value: any) => {
    const currentItems = formikRef.current.values.homePageDetailsAndWidgets.welcomeContentDetails || [];
    const updated = currentItems.map((row) => (row.id === itemId ? { ...row, [field]: value } : row));
    formikRef.current.setFieldValue('homePageDetailsAndWidgets.welcomeContentDetails', updated);
  }, []);

  const handleSubmitEdit: TableProps.SubmitEditFunction<WelcomeContentDetailsEntity> = useCallback((item, column, newValue) => {
    const columnId = column.id;
    if (!columnId) return;
    const currentItems = formikRef.current.values.homePageDetailsAndWidgets.welcomeContentDetails || [];
    const updated = currentItems.map((row) => (row.id === item.id ? { ...row, [columnId]: newValue as string } : row));
    formikRef.current.setFieldValue('homePageDetailsAndWidgets.welcomeContentDetails', updated);
  }, []);

  const handleAddRow = useCallback(() => {
    const currentItems = formikRef.current.values.homePageDetailsAndWidgets.welcomeContentDetails || [];
    const blank: WelcomeContentDetailsEntity = {
      id: uuidv4(),
      displayText: '',
      link: '',
      description: '',
      pointOfContact: ''
    };
    setItems([blank, ...currentItems]);
    setSelectedItems([blank]);
  }, [setItems]);

  const handleDeleteSelected = useCallback(() => {
    const currentItems = formikRef.current.values.homePageDetailsAndWidgets.welcomeContentDetails || [];
    const selectedIds = new Set(selectedItems.map((item) => item.id));
    setItems(currentItems.filter((item) => !selectedIds.has(item.id)));
    setSelectedItems([]);
  }, [selectedItems, setItems]);

  const handleMoveUp = useCallback(() => {
    if (selectedItems.length !== 1) return;
    const currentItems = formikRef.current.values.homePageDetailsAndWidgets.welcomeContentDetails || [];
    const idx = currentItems.findIndex((item) => item.id === selectedItems[0].id);
    if (idx <= 0) return;
    const updated = [...currentItems];
    [updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]];
    setItems(updated);
  }, [selectedItems, setItems]);

  const handleMoveDown = useCallback(() => {
    if (selectedItems.length !== 1) return;
    const currentItems = formikRef.current.values.homePageDetailsAndWidgets.welcomeContentDetails || [];
    const idx = currentItems.findIndex((item) => item.id === selectedItems[0].id);
    if (idx < 0 || idx >= currentItems.length - 1) return;
    const updated = [...currentItems];
    [updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]];
    setItems(updated);
  }, [selectedItems, setItems]);

  const columnDefinitions: TableProps.ColumnDefinition<WelcomeContentDetailsEntity>[] = useMemo(
    () => [
      {
        id: 'displayText',
        header: 'Display Name',
        width: 200,
        cell: (item) => item.displayText || '-',
        editConfig: {
          ariaLabel: 'Display Name',
          editIconAriaLabel: 'editable',
          errorIconAriaLabel: 'Display Name Error',
          editingCell: (item, { currentValue, setValue }) => (
            <Input
              autoFocus={true}
              value={currentValue ?? item.displayText ?? ''}
              onChange={(event) => setValue(event.detail.value)}
              placeholder="Enter display name"
            />
          ),
          validation: (item, value) => {
            const val = value ?? item.displayText;
            if (!val || val.trim().length === 0) {
              return 'Display name is required';
            }
            return undefined;
          }
        }
      },
      {
        id: 'link',
        header: 'Link',
        width: 200,
        maxWidth: 200,
        cell: (item) => item.link || '-',
        editConfig: {
          ariaLabel: 'Link',
          editIconAriaLabel: 'editable',
          errorIconAriaLabel: 'Link Error',
          editingCell: (item, { currentValue, setValue }) => (
            <Input
              autoFocus={true}
              value={currentValue ?? item.link ?? ''}
              onChange={(event) => setValue(event.detail.value)}
              placeholder="https://..."
            />
          ),
          validation: (item, value) => {
            const val = value ?? item.link ?? '';
            if (val && val.trim().length > 0 && !isValidUrl(val)) {
              return 'Must be a valid URL (http:// or https://)';
            }
            return undefined;
          }
        }
      },
      {
        id: 'pointOfContact',
        header: 'Point of Contact',
        width: 230,
        minWidth: 200,
        cell: (item) => {
          const value: SelectedEmployee | undefined = item.pointOfContact ? { login: item.pointOfContact, fullName: item.pointOfContact } : undefined;

          return (
            <div className="welcome-lookup-cell">
              <SingleEmployeeLookup
                label=""
                placeholder="Search by name"
                value={value}
                onChange={(emp) => {
                  handleUpdateField(item.id!, 'pointOfContact', emp ? emp.login : '');
                }}
              />
            </div>
          );
        }
      },
      {
        id: 'description',
        header: 'Description',
        width: 280,
        cell: (item) => (
          // SECURITY: Input sanitized via DOMPurify before rendering
          <div className="welcome-description-cell">
            {item.description ? <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.description) }} /> : '-'}
          </div>
        ),
        editConfig: {
          ariaLabel: 'Description',
          editIconAriaLabel: 'editable',
          errorIconAriaLabel: 'Description Error',
          editingCell: (item, { currentValue, setValue }) => (
            <Input
              autoFocus={true}
              value={currentValue ?? item.description ?? ''}
              onChange={(event) => setValue(event.detail.value)}
              placeholder="Brief description"
            />
          )
        }
      }
    ],
    [handleUpdateField]
  );

  const { homePageDetailsAndWidgets } = formik.values;
  const hasWelcomeContent = homePageDetailsAndWidgets?.hasWelcomeContent ?? false;

  return (
    <SpaceBetween size="m" direction="vertical">
      {/* Application Banner Configuration */}
      <ApplicationBannerSection formik={formik} />
      <Container
        variant="stacked"
        header={
          <Header
            variant="h2"
            description="Provide users with key quick links (e.g., documentation, wikis) to help introduce your app"
            actions={
              <SpaceBetween size="m" direction="horizontal">
                <Checkbox
                  disabled
                  checked={hasWelcomeContent}
                  onChange={({ detail }) => formik.setFieldValue('homePageDetailsAndWidgets.hasWelcomeContent', detail.checked)}
                  onBlur={() => formik.setFieldTouched('homePageDetailsAndWidgets.hasWelcomeContent', true)}
                >{`Enable`}</Checkbox>
              </SpaceBetween>
            }
          >
            Welcome
          </Header>
        }
      >
        {hasWelcomeContent && (
          <SpaceBetween size="m" direction="vertical">
            <Box padding={{ bottom: 'm' }}>
              <FormField
                constraintText={characterCountConstraintMessage(WELCOME_TITLE_MAX_LENGTH, homePageDetailsAndWidgets?.welcomeTitle?.length ?? 0)}
                errorText={formik.touched.homePageDetailsAndWidgets?.welcomeTitle && formik.errors.homePageDetailsAndWidgets?.welcomeTitle}
                stretch={true}
                label="Widget Title"
              >
                <Input
                  placeholder="Enter custom title"
                  onChange={({ detail }) => formik.setFieldValue('homePageDetailsAndWidgets.welcomeTitle', detail.value)}
                  onBlur={() => formik.setFieldTouched('homePageDetailsAndWidgets.welcomeTitle', true)}
                  value={homePageDetailsAndWidgets?.welcomeTitle ?? ''}
                />
              </FormField>
            </Box>

            <div className="welcome-table-wrapper">
              <Table
                columnDefinitions={columnDefinitions}
                items={welcomeContentDetails}
                submitEdit={handleSubmitEdit}
                selectionType="multi"
                selectedItems={selectedItems}
                onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
                trackBy="id"
                ariaLabels={{
                  activateEditLabel: (column, item) => `Edit ${item.displayText} ${column.header}`,
                  cancelEditLabel: (column) => `Cancel editing ${column.header}`,
                  submitEditLabel: (column) => `Submit editing ${column.header}`,
                  tableLabel: 'Welcome links table with inline editing'
                }}
                empty={
                  <Box textAlign="center" color="inherit">
                    <b>No links</b>
                    <Box variant="p" color="inherit">
                      Click &quot;Add link&quot; to create a new welcome link entry.
                    </Box>
                  </Box>
                }
                header={
                  <Header
                    variant="h2"
                    counter={`(${welcomeContentDetails.length})`}
                    actions={
                      <SpaceBetween size="xs" direction="horizontal">
                        <Button iconName="add-plus" onClick={handleAddRow}>
                          Add link
                        </Button>
                        <Button onClick={handleDeleteSelected} disabled={selectedItems.length === 0}>
                          Delete
                        </Button>
                        <Button onClick={handleMoveUp} disabled={selectedItems.length !== 1} iconName="angle-up">
                          Move up
                        </Button>
                        <Button onClick={handleMoveDown} disabled={selectedItems.length !== 1} iconName="angle-down">
                          Move down
                        </Button>
                      </SpaceBetween>
                    }
                  >
                    Displayed Links
                  </Header>
                }
              />
            </div>
          </SpaceBetween>
        )}
      </Container>
    </SpaceBetween>
  );
};

const ApplicationBannerSection: React.FC<{ formik: FormikProps<HomePageWizard> }> = ({ formik }) => {
  const banner = formik.values.homePageDetailsAndWidgets.applicationBanner;
  const bannerErrors = formik.errors.homePageDetailsAndWidgets?.applicationBanner as Record<string, string> | undefined;
  const bannerTouched = formik.touched.homePageDetailsAndWidgets?.applicationBanner as Record<string, boolean> | undefined;
  const [hasAction, setHasAction] = useState(!!banner?.action?.label || !!banner?.action?.url);

  const buildPreviewItems = (): FlashbarProps.MessageDefinition[] => {
    if (!banner?.isEnabled) return [];
    return [
      {
        type: banner.bannerType as FlashbarProps.Type,
        header: banner.header,
        content: banner.content ? <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(banner.content) }} /> : undefined,
        dismissible: banner.dismissible,
        onDismiss: () => {},
        action:
          hasAction && banner.action?.label ? (
            <Button href={banner.action.url} target="_blank" iconName="external" iconAlign="right">
              {banner.action.label}
            </Button>
          ) : undefined
      }
    ];
  };

  const handleActionToggle = (checked: boolean) => {
    setHasAction(checked);
    if (!checked) {
      formik.setFieldValue('homePageDetailsAndWidgets.applicationBanner.action', undefined);
    } else {
      formik.setFieldValue('homePageDetailsAndWidgets.applicationBanner.action', { label: '', url: '' });
    }
  };

  const actionErrors = bannerErrors?.action as unknown as Record<string, string> | undefined;
  const actionTouched = bannerTouched?.action as unknown as Record<string, boolean> | undefined;

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Display a site-wide banner notification to all users (e.g., maintenance alerts, important updates)"
          actions={
            <Toggle
              onChange={({ detail }) => formik.setFieldValue('homePageDetailsAndWidgets.applicationBanner.isEnabled', detail.checked)}
              checked={banner?.isEnabled || false}
            >
              Enable
            </Toggle>
          }
        >
          Application Banner
        </Header>
      }
    >
      {banner?.isEnabled && (
        <SpaceBetween size="l" direction="vertical">
          <SpaceBetween size="m" direction="vertical">
            <FormField label="Banner type">
              <Select
                selectedOption={BANNER_TYPE_OPTIONS.find((opt) => opt.value === banner.bannerType) || BANNER_TYPE_OPTIONS[0]}
                options={BANNER_TYPE_OPTIONS}
                onChange={({ detail }) => formik.setFieldValue('homePageDetailsAndWidgets.applicationBanner.bannerType', detail.selectedOption.value)}
              />
            </FormField>

            <FormField
              label={
                <>
                  Header<i> - optional</i>
                </>
              }
              errorText={bannerTouched?.header && bannerErrors?.header}
              constraintText={characterCountConstraintMessage(100, banner.header?.length ?? 0)}
            >
              <Input
                placeholder="e.g., Service Status - WARNING"
                value={banner.header || ''}
                onChange={({ detail }) => formik.setFieldValue('homePageDetailsAndWidgets.applicationBanner.header', detail.value)}
                onBlur={() => formik.setFieldTouched('homePageDetailsAndWidgets.applicationBanner.header', true)}
              />
            </FormField>

            <CustomQuillEditor
              name="homePageDetailsAndWidgets.applicationBanner.content"
              label="Message content"
              value={banner.content || ''}
              onChange={(name, value) => {
                const cleaned = value?.replace(/(<p><br><\/p>)+$/g, '') || '';
                formik.setFieldValue(name, cleaned);
              }}
              onBlur={() => formik.setFieldTouched('homePageDetailsAndWidgets.applicationBanner.content', true)}
              errorText={bannerTouched?.content ? bannerErrors?.content : undefined}
              quillConfig={BANNER_QUILL_CONFIG}
            />

            <Checkbox
              checked={banner.dismissible ?? true}
              onChange={({ detail }) => formik.setFieldValue('homePageDetailsAndWidgets.applicationBanner.dismissible', detail.checked)}
            >
              Allow users to dismiss
            </Checkbox>

            <FormField
              label={
                <>
                  Action button<i> - optional</i>
                </>
              }
              description="Add a link button to the banner"
            >
              <SpaceBetween size="m" direction="vertical">
                <Toggle checked={hasAction} onChange={({ detail }) => handleActionToggle(detail.checked)}>
                  Enable action button
                </Toggle>
                {hasAction && (
                  <SpaceBetween size="s" direction="vertical">
                    <FormField label="Button label" errorText={actionTouched?.label && actionErrors?.label}>
                      <Input
                        placeholder="e.g., Learn more"
                        value={banner.action?.label || ''}
                        onChange={({ detail }) => formik.setFieldValue('homePageDetailsAndWidgets.applicationBanner.action.label', detail.value)}
                        onBlur={() => formik.setFieldTouched('homePageDetailsAndWidgets.applicationBanner.action.label', true)}
                      />
                    </FormField>
                    <FormField label="Button URL" errorText={actionTouched?.url && actionErrors?.url}>
                      <Input
                        placeholder="https://..."
                        value={banner.action?.url || ''}
                        onChange={({ detail }) => formik.setFieldValue('homePageDetailsAndWidgets.applicationBanner.action.url', detail.value)}
                        onBlur={() => formik.setFieldTouched('homePageDetailsAndWidgets.applicationBanner.action.url', true)}
                      />
                    </FormField>
                  </SpaceBetween>
                )}
              </SpaceBetween>
            </FormField>
          </SpaceBetween>

          {/* Live Preview */}
          <Container header={<Header variant="h3">Preview</Header>}>
            <Flashbar items={buildPreviewItems()} />
          </Container>
        </SpaceBetween>
      )}
    </Container>
  );
};

export default HomePageContentStep1;
